import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LayoutDashboard, ListTodo, Calendar, Users, FileText, Settings } from 'lucide-react';
import OpsOverview from '../components/ops/OpsOverview';
import OpsTasks from '../components/ops/OpsTasks';
import OpsSchedule from '../components/ops/OpsSchedule';
import OpsTeam from '../components/ops/OpsTeam';
import OpsReport from '../components/ops/OpsReport';
import OpsSettings from '../components/ops/OpsSettings';

const OpsManagerPage = () => {
  const [activeTab, setActiveTab] = useState(() => {
    // Load from localStorage or default to overview
    return localStorage.getItem('ops_manager_tab_preference') || 'overview';
  });

  // Persist tab selection to localStorage
  useEffect(() => {
    localStorage.setItem('ops_manager_tab_preference', activeTab);
  }, [activeTab]);

  const tabs = [
    { value: 'overview', label: 'Overview', icon: LayoutDashboard },
    { value: 'tasks', label: 'Tasks', icon: ListTodo },
    { value: 'schedule', label: 'Schedule', icon: Calendar },
    { value: 'team', label: 'Team', icon: Users },
    { value: 'report', label: 'Reports', icon: FileText },
    { value: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Section */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#247C75] to-[#1d6259] flex items-center justify-center">
                  <LayoutDashboard className="h-6 w-6 text-white" />
                </div>
                Cleanifax Ops Manager
              </h1>
              <p className="text-slate-600 mt-1">
                AI-powered task distribution, scheduling & workload optimization
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Full-Width Tab Navigation */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-8">
          <nav className="flex items-center gap-1" role="tablist">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.value;
              
              return (
                <button
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  role="tab"
                  aria-selected={isActive}
                  className={`
                    flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all
                    border-b-2 -mb-[1px]
                    ${isActive 
                      ? 'border-[#247C75] text-[#247C75]' 
                      : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }
                    rounded-t-lg
                  `}
                >
                  <Icon className={`h-4 w-4 ${isActive ? 'text-[#247C75]' : 'text-slate-400'}`} />
                  <span className={isActive ? 'font-semibold' : 'font-medium'}>
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-[1600px] mx-auto px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsContent value="overview" className="mt-0">
            <OpsOverview />
          </TabsContent>

          <TabsContent value="tasks" className="mt-0">
            <OpsTasks />
          </TabsContent>

          <TabsContent value="schedule" className="mt-0">
            <OpsSchedule />
          </TabsContent>

          <TabsContent value="team" className="mt-0">
            <OpsTeam />
          </TabsContent>

          <TabsContent value="report" className="mt-0">
            <OpsReport />
          </TabsContent>

          <TabsContent value="settings" className="mt-0">
            <OpsSettings />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default OpsManagerPage;
